import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COUNTRIES, flagFromCode } from '@/lib/countries';
import { useTranslation } from 'react-i18next';
import { useAuth, api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  MapPin, Loader2, LogOut, Save, ShieldBan, Trash2, Crown, FileText,
  Shield, AlertTriangle, ChevronRight, Bell, Sparkles, BadgeCheck, ShieldCheck, User, CreditCard, Zap, Ruler, LifeBuoy, Send, EyeOff, Gift, Phone, Cookie, Users, Globe, CheckCheck, X, Mail, FlaskConical
} from 'lucide-react';
import { getUnit, setUnit as saveUnit } from '@/utils/units';
import BackNavButton from '@/components/BackNavButton';
import PhoneAuthPanel from '@/components/PhoneAuthPanel';
import EmailVerificationDialog from '@/components/EmailVerificationDialog';
import { demoMode } from '@/lib/demoMode';
import { FIXTURE_STATS } from '@/lib/fixtures';

// Language display labels — matches SUPPORTED_LANGUAGES in backend/routes/settings.py.
const LANG_LABEL = { en: 'English', es: 'Español', 'pt-BR': 'Português (BR)', fr: 'Français' };
// Static, keeps i18n bundle small: labels stay in each language's native form.

const Row = ({ icon: Icon, title, desc, value, onClick, testId, danger, accent }) => (
  <button onClick={onClick} data-testid={testId}
    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-black/[0.02] transition-colors text-left group">
    {Icon && (
      <Icon size={17} strokeWidth={1.6}
        className={`flex-shrink-0 transition-colors ${danger ? 'text-red-500' : accent ? 'text-[#B91C1C]' : 'text-[#3F3F46] group-hover:text-[#0F0F10]'}`} />
    )}
    <div className="flex-1 min-w-0">
      <p className={`text-[14px] font-semibold tracking-tight ${danger ? 'text-red-600' : 'text-[#0F0F10]'}`}>{title}</p>
      {desc && <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{desc}</p>}
    </div>
    {value && <span className="text-[11px] text-[#8A8A93] font-medium tabular-nums">{value}</span>}
    <ChevronRight size={14} className="text-[#C4C4CB] group-hover:text-[#4B4B52] group-hover:translate-x-0.5 transition-all flex-shrink-0" strokeWidth={2} />
  </button>
);

const SectionGroup = ({ title, children, ...rest }) => (
  <section {...rest}>
    {title && (
      <div className="flex items-center gap-3 px-1 mb-3">
        <span className="font-brand text-[18px] font-[500] tracking-tight text-[#0F0F10]">{title}</span>
        <div className="flex-1 h-px bg-[var(--hairline)]" />
      </div>
    )}
    <div className="bg-white border border-[var(--hairline)] rounded-2xl overflow-hidden divide-y divide-[var(--hairline)] premium-card">
      {children}
    </div>
  </section>
);

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loc, setLoc] = useState({ city: '', country: '', lat: 0, lng: 0 });
  const [countryBannerDismissed, setCountryBannerDismissed] = useState(() => {
    try { return localStorage.getItem('mtk_country_dismissed_v1') === '1'; }
    catch { return false; }
  });
  const [radius, setRadius] = useState(50);
  const [blocks, setBlocks] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [blocksOpen, setBlocksOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportForm, setSupportForm] = useState({ category: 'question', subject: '', message: '' });
  const [deleting, setDeleting] = useState(false);
  // Sprint D — language picker (mobile parity).
  const [langOpen, setLangOpen] = useState(false);
  const [langSaving, setLangSaving] = useState(false);
  const [subTier, setSubTier] = useState('free');
  const [boostInfo, setBoostInfo] = useState(null);
  const [boosting, setBoosting] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [incognito, setIncognito] = useState(false);
  const [incognitoLoading, setIncognitoLoading] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [pushPrefs, setPushPrefs] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/settings').then(r => { setSettings(r.data); if (r.data.location) setLoc(r.data.location); setRadius(r.data.location_radius || 50); }),
      api.get('/blocks').then(r => setBlocks(r.data.blocks || [])),
      api.get('/subscriptions/my').then(r => setSubTier(r.data.subscription?.tier || 'free')),
      api.get('/boost/status').then(r => setBoostInfo(r.data)).catch(() => {}),
      api.get('/store/inventory').then(r => setInventory(r.data)).catch(() => {}),
      api.get('/profile').then(r => setIncognito(!!r.data.incognito_enabled)).catch(() => {}),
      api.get('/email/prefs').then(r => setEmailPrefs(r.data.prefs)).catch(() => {}),
      api.get('/notifications/push-prefs').then(r => setPushPrefs(r.data.prefs)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateEmailPref = async (key, value) => {
    const prev = emailPrefs;
    setEmailPrefs(p => ({ ...p, [key]: value }));
    try {
      const r = await api.put('/email/prefs', { [key]: value });
      setEmailPrefs(r.data.prefs);
    } catch (e) {
      setEmailPrefs(prev);
      toast.error('Could not update preference');
    }
  };

  const updatePushPref = async (key, value) => {
    const prev = pushPrefs;
    setPushPrefs(p => ({ ...p, [key]: value }));
    try {
      const r = await api.put('/notifications/push-prefs', { [key]: value });
      setPushPrefs(r.data.prefs);
    } catch (e) {
      setPushPrefs(prev);
      toast.error('Could not update preference');
    }
  };

  const activateBoost = async () => {
    setBoosting(true);
    try {
      const r = await api.post('/boost/activate');
      setBoostInfo(b => ({...b, active:true, seconds_remaining: r.data.minutes*60, boosts_remaining: r.data.boosts_remaining}));
      toast.success(`Meet Priority active for ${r.data.minutes} minutes!`);
      // refresh inventory after boost consume
      api.get('/store/inventory').then(r => setInventory(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Meet Priority failed'); }
    finally { setBoosting(false); }
  };

  const toggleIncognito = async (next) => {
    setIncognitoLoading(true);
    try {
      await api.post('/settings/incognito', { enabled: next });
      setIncognito(next);
      toast.success(next ? 'Incognito browsing ON — your views are now hidden.' : 'Incognito browsing OFF');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upgrade to Premium to go incognito'); }
    finally { setIncognitoLoading(false); }
  };

  const saveLoc = async () => {
    if (!loc.city) { toast.error('Enter a city'); return; }
    setSaving(true);
    try {
      const r = await api.put('/settings/location', { ...loc, radius });
      if (r.data.location) setLoc(r.data.location);
      toast.success(r.data.geocoded ? `Saved · ${r.data.location.city} detected` : 'Location saved');
      setLocOpen(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const detect = () => {
    navigator.geolocation?.getCurrentPosition(
      async (p) => {
        const lat = p.coords.latitude, lng = p.coords.longitude;
        setLoc((l) => ({ ...l, lat, lng }));
        toast.success('Location detected');
        try {
          const r = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}`);
          const d = r.data || {};
          setLoc((l) => ({
            ...l,
            city:    d.city    || l.city,
            country: d.country || l.country,
            lat, lng,
          }));
          if (d.country) {
            const flag = flagFromCode(d.country_code);
            const bits = [d.city, d.country].filter(Boolean).join(', ');
            toast.success(`📍 Detected: ${bits} ${flag}`.trim());
          }
        } catch { /* ignore — user can still edit manually */ }
      },
      () => toast.error('Failed to detect location')
    );
  };

  const unblock = async (uid) => {
    try { await api.delete(`/block/${uid}`); setBlocks(b => b.filter(x => x.blocked_id !== uid)); toast.success('Unblocked'); } catch { /* ignore */ }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      localStorage.removeItem('session_token');
      toast.success('Account deleted');
      window.location.href = '/';
    } catch { toast.error('Failed to delete'); setDeleting(false); }
  };

  const doLogout = async () => { await logout(); window.location.href = '/'; };

  const openBillingPortal = async () => {
    try {
      const r = await api.post('/payments/portal', { origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Unable to open billing portal'); }
  };

  const urlB64ToUint8Array = (b64) => {
    const pad = '='.repeat((4 - b64.length % 4) % 4);
    const base64 = (b64 + pad).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };

  const requestNotifications = async () => {
    if (!('Notification' in window)) { toast.error('Notifications not supported on this browser'); return; }
    if (!('serviceWorker' in navigator)) { toast.error('Push not supported on this browser'); return; }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Permission denied — enable in browser settings'); return; }
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) { toast.error('Push not configured'); return; }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(vapidKey)
        });
      }
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      toast.success(t('settings.pushEnabled'));
      new Notification('Meet to Keep', { body: t('settings.pushNewBody'), icon: '/logo.png' });
    } catch (e) {
      console.error('Push subscribe failed:', e);
      toast.error(e.message || t('settings.pushEnableFailed'));
    }
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;

  const tierLabel = { free: t('settings.planFree'), premium: t('settings.planPremium'), vip: t('settings.planVip') }[subTier] || subTier;

  return (
    <div className="min-h-screen bg-[var(--surface-2)] pb-[96px]" data-testid="settings-page">
      {/* Editorial header */}
      <div className="bg-white border-b border-[var(--hairline)]">
        <div className="px-5 pt-7 pb-5 flex items-center gap-3">
          <BackNavButton />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A93] font-semibold">{t('settings.kicker')}</p>
            <h1 className="font-brand text-[30px] font-[500] tracking-tight text-[#0F0F10] leading-tight">{t('settings.title')}</h1>
          </div>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-7">
        {/* Profile identity card */}
        <button onClick={() => navigate('/profile')} data-testid="profile-pill"
          className="w-full bg-white border border-[var(--hairline)] rounded-2xl p-5 flex items-center gap-4 hover:bg-black/[0.01] transition-colors text-left premium-card group">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-[var(--hairline-strong)]">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User size={22} className="text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-[#0F0F10] tracking-tight">{settings?.name || user?.name}</p>
            <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{settings?.email || user?.email}</p>
          </div>
          <ChevronRight size={16} className="text-[#C4C4CB] group-hover:text-[#4B4B52] group-hover:translate-x-0.5 transition-all" strokeWidth={2} />
        </button>

        {/* Country backfill nudge — dismissible, only shows when the user
            has a city but no country yet. Powers the Admin Ratios country
            panel by encouraging existing users to add their country. */}
        {loc.city && !loc.country && !countryBannerDismissed && (
          <div
            className="rounded-2xl flex items-start gap-3 relative"
            style={{ background: '#FEF7ED', border: '1px solid #B38138', padding: '14px 40px 14px 14px' }}
            data-testid="country-backfill-banner"
          >
            <Globe size={18} style={{ color: '#B38138' }} strokeWidth={2.2} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-bold text-[#7C2D12] leading-tight">Add your country</p>
              <p className="text-[12px] text-[#78350F] leading-snug mt-0.5">
                Help us match you across borders and improve our community insights. Takes 5 seconds.
              </p>
              <button
                type="button"
                onClick={() => setLocOpen(true)}
                data-testid="country-backfill-cta"
                className="mt-2 rounded-full text-white font-semibold hover:brightness-110 transition-all"
                style={{ background: '#B38138', padding: '6px 12px', fontSize: 12 }}
              >
                Pick country
              </button>
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => {
                try { localStorage.setItem('mtk_country_dismissed_v1', '1'); } catch { /* ignore */ }
                setCountryBannerDismissed(true);
              }}
              data-testid="country-backfill-dismiss"
              className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/[0.05]"
            >
              <X size={14} style={{ color: '#78350F' }} />
            </button>
          </div>
        )}

        {/* Membership — editorial hero card */}
        <SectionGroup title={t('settings.membershipSection')}>
          <button onClick={() => navigate('/subscription')} data-testid="subscription-link"
            className="w-full px-5 py-5 hover:bg-black/[0.02] transition-colors text-left group">
            <div className="flex items-start gap-4">
              <Crown size={18} strokeWidth={1.5} className={`mt-0.5 flex-shrink-0 ${subTier === 'free' ? 'text-[#3F3F46]' : 'text-[#B38138]'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A93] font-semibold">{t('settings.currentPlan')}</p>
                <p className="font-brand text-[24px] font-[500] tracking-tight text-[#0F0F10] mt-0.5 leading-tight">
                  {tierLabel}
                </p>
                <p className="text-[12px] text-[#8A8A93] mt-1.5 leading-relaxed">
                  {subTier === 'free' ? t('settings.freeUpsell') : t('settings.paidPitch')}
                </p>
              </div>
              <span className={`text-[11px] font-bold tracking-wide px-3.5 py-1.5 rounded-full self-start mt-1 transition-transform group-hover:scale-[1.03] ${subTier === 'free' ? 'brand-gradient text-white' : 'text-[#B38138] bg-[rgba(179,129,56,0.1)] border border-[#B38138]/20'}`}>
                {subTier === 'free' ? t('settings.upgrade') : t('settings.manage')}
              </span>
            </div>
          </button>
          {subTier !== 'free' && (
            <Row testId="billing-portal-link" icon={CreditCard}
              title={t('settings.billingTitle')} desc={t('settings.billingDesc')} onClick={openBillingPortal} />
          )}
          {subTier !== 'free' && (
            <button onClick={activateBoost} disabled={boosting || boostInfo?.active || ((boostInfo?.boosts_remaining ?? 0) <= 0 && (inventory?.purchased_boosts ?? 0) <= 0)}
              data-testid="boost-activate" className="w-full flex items-center gap-4 px-5 py-4 hover:bg-black/[0.02] transition-colors text-left group disabled:opacity-60 disabled:cursor-not-allowed">
              <Zap size={17} strokeWidth={1.6} className={`flex-shrink-0 ${boostInfo?.active ? 'text-[#B38138]' : 'text-[#3F3F46]'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">{t('settings.meetPriorityTitle')}</p>
                <p className="text-[12px] text-[#8A8A93] mt-0.5">
                  {boostInfo?.active ? t('settings.meetPriorityActive', { minutes: Math.ceil((boostInfo.seconds_remaining||0)/60) }) :
                   (boostInfo?.boosts_remaining ?? 0) > 0 ? t('settings.meetPriorityWeekly', { count: boostInfo.boosts_remaining }) :
                   (inventory?.purchased_boosts ?? 0) > 0 ? t('settings.meetPriorityPurchased', { count: inventory.purchased_boosts }) :
                   t('settings.meetPriorityUsed')}
                </p>
              </div>
              <span className="text-[10px] font-bold tracking-wide text-[#B38138] bg-[rgba(179,129,56,0.1)] border border-[#B38138]/20 px-2.5 py-1 rounded-full">
                {boosting ? '…' : boostInfo?.active ? t('settings.boostActive') : t('settings.boostActivate')}
              </span>
            </button>
          )}
          <Row testId="store-link" icon={Sparkles}
            title={t('settings.storeTitle')}
            desc={inventory ? t('settings.storeInventoryDesc', { boosts: inventory.purchased_boosts || 0, likes: inventory.purchased_super_likes || 0 }) : t('settings.storeDefaultDesc')}
            onClick={() => navigate('/store')} />
        </SectionGroup>

        {/* Grow your profile */}
        <SectionGroup title={t('settings.growSection')}>
          <Row testId="edit-profile-link" icon={Sparkles} accent
            title={t('settings.editProfileTitle')} desc={t('settings.editProfileDesc')}
            onClick={() => navigate('/profile?edit=1')} />
          <Row testId="invite-friends-link" icon={Gift}
            title={t('settings.inviteTitle')} desc={t('settings.inviteDesc')}
            onClick={() => navigate('/referrals')} />
          <Row testId="profile-score-link" icon={Sparkles}
            title={t('settings.profileScoreTitle')} desc={t('settings.profileScoreDesc')} onClick={() => navigate('/profile-score')} />
          <Row testId="verify-link" icon={BadgeCheck}
            title={t('settings.verifyTitle')} desc={t('settings.verifyDesc')} onClick={() => navigate('/verify')} />
          <Row testId="credibility-link" icon={ShieldCheck}
            title={t('settings.credibilityTitle')} desc={t('settings.credibilityDesc')} onClick={() => navigate('/credibility')} />
        </SectionGroup>

        {/* Safety */}
        <SectionGroup title={t('settings.safetySection')}>
          {/* Feb 2026 — Email verification row for stranded email-provider
              users. The onboarding EmailVerifyBanner is the only OTHER surface
              that exposes this — if the user dismissed it or never revisited
              /profile/setup, they had no way to resend from anywhere. Google
              users are skipped (their emails are pre-verified via OAuth). */}
          {user && !user.email_verified && user.auth_provider !== 'google' && (
            <button onClick={() => setEmailVerifyOpen(true)} data-testid="email-verify-link"
              className="w-full flex items-center gap-4 px-5 py-4 hover:bg-black/[0.02] transition-colors text-left group">
              <Mail size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#B45309]" />
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10] inline-flex items-center gap-1.5">
                  Verify your email
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-[#B45309] bg-[#B45309]/10 px-1.5 py-0.5 rounded-full">
                    <AlertTriangle size={9} /> Not verified
                  </span>
                </p>
                <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">
                  {user.email ? `We'll resend the 6-digit code to ${user.email}` : 'Enter or resend your 6-digit code'}
                </p>
              </div>
              <ChevronRight size={14} className="text-[#C4C4CB] group-hover:text-[#4B4B52] group-hover:translate-x-0.5 transition-all flex-shrink-0" strokeWidth={2} />
            </button>
          )}
          <PhoneRow settings={settings} onLinkClick={() => setPhoneOpen(true)} />
          <Row testId="safety-center-hub-link" icon={ShieldCheck} accent
            title={t('settings.safetyCenterTitle')} desc={t('settings.safetyCenterDesc')} onClick={() => navigate('/safety')} />
          <Row testId="safe-meet-link" icon={Shield}
            title={t('settings.safeMeetTitle')} desc={t('settings.safeMeetDesc')} onClick={() => navigate('/safe-meet')} />
          <Row testId="blocked-users-btn" icon={ShieldBan}
            title={t('settings.blockedTitle')} desc={blocks.length ? t('settings.blockedDescCount', { count: blocks.length }) : t('settings.blockedDescDefault')}
            onClick={() => setBlocksOpen(true)} />
          {/* Incognito browsing — Premium only */}
          <div className="w-full flex items-center gap-4 px-5 py-4" data-testid="incognito-row">
            <EyeOff size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#3F3F46]" />
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10] inline-flex items-center gap-1.5">
                {t('settings.incognitoTitle')}
                {subTier === 'free' && <Crown size={11} className="text-[#B38138]" />}
              </p>
              <p className="text-[12px] text-[#8A8A93] mt-0.5">
                {subTier === 'free' ? t('settings.incognitoFreeDesc') : incognito ? t('settings.incognitoOnDesc') : t('settings.incognitoOffDesc')}
              </p>
            </div>
            {subTier === 'free' ? (
              <button onClick={() => navigate('/subscription')} className="text-[10px] font-bold tracking-wide text-[#B38138] bg-[rgba(179,129,56,0.1)] border border-[#B38138]/20 px-2.5 py-1 rounded-full">{t('settings.upgrade')}</button>
            ) : (
              <button onClick={() => toggleIncognito(!incognito)} disabled={incognitoLoading}
                data-testid="incognito-toggle"
                className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${incognito ? 'bg-[#1E4FD8]' : 'bg-[#E5E5E7]'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all ${incognito ? 'left-5' : 'left-0.5'}`}></span>
              </button>
            )}
          </div>
        </SectionGroup>

        {/* Preferences */}
        <SectionGroup title={t('settings.prefsSection')}>
          <Row testId="meeting-prefs-link" icon={Sparkles}
            title={t('settings.meetingPrefsTitle')} desc={t('settings.meetingPrefsDesc')}
            onClick={() => navigate('/meeting-prefs')} />
          <Row testId="open-location" icon={MapPin}
            title={t('settings.locationTitle')} desc={loc.city ? t('settings.locationDescSet', { city: loc.city, radius }) : t('settings.locationDescDefault')}
            onClick={() => setLocOpen(true)} />
          <Row testId="open-travel" icon={Sparkles}
            title="Travel & Cities" desc="Travel Mode, Multi-City, and Hometown"
            onClick={() => navigate('/travel')} />
          {/* Aug 2026 mobile parity — new privacy/security/account rows */}
          <Row testId="settings-privacy-visibility" icon={EyeOff}
            title="Privacy & Visibility" desc="Pause profile · Discover · online status · distance · read receipts"
            onClick={() => navigate('/settings/privacy-visibility')} />
          <Row testId="settings-login-security" icon={ShieldCheck}
            title="Login & Security" desc="Two-factor auth · Active sessions"
            onClick={() => navigate('/settings/login-security')} />
          <Row testId="settings-data-privacy" icon={FileText}
            title="Data & Privacy" desc="Export · Erase activity · Ad preferences"
            onClick={() => navigate('/settings/data-privacy')} />
          <Row testId="settings-deactivate" icon={AlertTriangle}
            title="Deactivate Account" desc="Take a 30-day break, or delete permanently"
            onClick={() => navigate('/settings/deactivate')} />
          <Row testId="language-link" icon={Globe}
            title={t('settings.languageTitle')} desc={LANG_LABEL[settings?.language || 'en'] || 'English'}
            onClick={() => setLangOpen(true)} />
          <Row testId="enable-notifications" icon={Bell}
            title={t('settings.pushTitle')} desc={t('settings.pushDesc')}
            onClick={requestNotifications} />
          <NearbyAlertsRow settings={settings} setSettings={setSettings} />
          <ReadReceiptsRow settings={settings} setSettings={setSettings} />
          <UnitRow />
        </SectionGroup>

        {/* Email Notifications — fine-grained category prefs */}
        <SectionGroup title={t('settings.emailSection')} data-testid="email-prefs-section" id="email-prefs">
          <EmailPrefRow testId="email-pref-matches" label={t('settings.emailMatchesLabel')}
            desc={t('settings.emailMatchesDesc')}
            checked={emailPrefs?.matches} onChange={v => updateEmailPref('matches', v)} />
          <EmailPrefRow testId="email-pref-messages" label={t('settings.emailMessagesLabel')}
            desc={t('settings.emailMessagesDesc')}
            checked={emailPrefs?.messages} onChange={v => updateEmailPref('messages', v)} />
          <EmailPrefRow testId="email-pref-likes" label={t('settings.emailLikesLabel')}
            desc={t('settings.emailLikesDesc')}
            checked={emailPrefs?.likes} onChange={v => updateEmailPref('likes', v)} />
          <EmailPrefRow testId="email-pref-intros" label={t('settings.emailIntrosLabel')}
            desc={t('settings.emailIntrosDesc')}
            checked={emailPrefs?.intros} onChange={v => updateEmailPref('intros', v)} />
          <EmailPrefRow testId="email-pref-views" label={t('settings.emailViewsLabel')}
            desc={t('settings.emailViewsDesc')}
            checked={emailPrefs?.profile_views} onChange={v => updateEmailPref('profile_views', v)} />
          <EmailPrefRow testId="email-pref-weekly" label={t('settings.emailWeeklyLabel')}
            desc={t('settings.emailWeeklyDesc')}
            checked={emailPrefs?.weekly_digest} onChange={v => updateEmailPref('weekly_digest', v)} />
          <EmailPrefRow testId="email-pref-marketing" label={t('settings.emailMarketingLabel')}
            desc={t('settings.emailMarketingDesc')}
            checked={emailPrefs?.marketing} onChange={v => updateEmailPref('marketing', v)} />
        </SectionGroup>

        {/* Push Notification Preferences — per-category toggles for native/web push */}
        <SectionGroup title="Push notifications" data-testid="push-prefs-section" id="push-prefs">
          <EmailPrefRow testId="push-pref-matches" label="New matches"
            desc="Ping me when we match with someone."
            checked={pushPrefs?.matches !== false} onChange={v => updatePushPref('matches', v)} />
          <EmailPrefRow testId="push-pref-messages" label="New messages"
            desc="Ping me when a match sends a message."
            checked={pushPrefs?.messages !== false} onChange={v => updatePushPref('messages', v)} />
          <EmailPrefRow testId="push-pref-likes" label="Likes"
            desc="Ping me when someone likes my profile."
            checked={pushPrefs?.likes !== false} onChange={v => updatePushPref('likes', v)} />
          <EmailPrefRow testId="push-pref-intros" label="Intros"
            desc="Ping me when someone sends a personal intro."
            checked={pushPrefs?.intros !== false} onChange={v => updatePushPref('intros', v)} />
        </SectionGroup>

        {/* Support & Legal */}
        <SectionGroup title={t('settings.supportSection')}>
          <Row testId="contact-support" icon={LifeBuoy}
            title={t('settings.contactSupportTitle')} desc={t('settings.contactSupportDesc')}
            onClick={() => setSupportOpen(true)} />
          <Row testId="guidelines-link" icon={Users}
            title={t('settings.guidelinesTitle')} onClick={() => navigate('/guidelines')} />
          <Row testId="privacy-link" icon={Shield}
            title={t('settings.privacyTitle')} onClick={() => navigate('/privacy')} />
          <Row testId="terms-link" icon={FileText}
            title={t('settings.termsTitle')} onClick={() => navigate('/terms')} />
          <Row testId="cookies-link" icon={Cookie}
            title={t('settings.cookiesTitle')} onClick={() => navigate('/cookies')} />
        </SectionGroup>

        {/* Developer — Client-Side Demo Preview toggle (Feb 2026). Frontend-only
            fixture layer so mobile can regression-test Matches / Likes / Messages
            / Views UI without touching the backend. See /lib/fixtures.js. */}
        <SectionGroup title="Developer">
          <DemoModeRow />
        </SectionGroup>

        {/* Account actions — graphite/red, no pastels */}
        <div className="pt-1 grid grid-cols-2 gap-3">
          <button onClick={doLogout} data-testid="logout-btn"
            className="py-3 rounded-full bg-white border border-[var(--hairline-strong)] text-[#0F0F10] font-semibold text-[13px] tracking-tight hover:bg-black/[0.03] hover:border-black/20 transition-all inline-flex items-center justify-center gap-2">
            <LogOut size={14} strokeWidth={2} /> {t('settings.signOut')}
          </button>
          <button onClick={() => setDeleteOpen(true)} data-testid="delete-account-btn"
            className="py-3 rounded-full bg-white border border-red-500/20 text-red-600 font-semibold text-[13px] tracking-tight hover:bg-red-50 hover:border-red-500/40 transition-all inline-flex items-center justify-center gap-2">
            <Trash2 size={14} strokeWidth={2} /> {t('settings.deleteAccount')}
          </button>
        </div>

        <p className="text-center text-[10px] text-[#C4C4CB] tracking-wider uppercase pt-3 font-medium">{t('settings.footerVersion')}</p>
      </div>

      {/* Phone linking dialog */}
      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="phone-link-dialog">
          <div className="w-12 h-12 rounded-full bg-[rgba(185,28,28,0.08)] flex items-center justify-center mb-4">
            <Phone className="w-6 h-6 text-[#B91C1C]" />
          </div>
          <DialogTitle className="font-display text-xl font-[600] mb-1">
            {settings?.phone_verified ? t('settings.phoneDialogTitleChange') : t('settings.phoneDialogTitleAdd')}
          </DialogTitle>
          <p className="text-sm text-[#4B4B52] mb-5 leading-relaxed">
            {t('settings.phoneDialogBody')}
          </p>
          <PhoneAuthPanel
            mode="link"
            onAuthSuccess={(res) => {
              setSettings((s) => ({ ...(s || {}), phone_number: res.phone_number, phone_verified: true }));
              setPhoneOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Email verification dialog (opens from the Safety
          section row when the user's email isn't verified). */}
      <EmailVerificationDialog open={emailVerifyOpen} onClose={() => setEmailVerifyOpen(false)} />

      {/* Language picker dialog — Sprint D */}
      <Dialog open={langOpen} onOpenChange={setLangOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="language-dialog">
          <DialogTitle className="font-display text-xl font-[600] mb-1">{t('settings.languageTitle')}</DialogTitle>
          <p className="text-xs text-[#8A8A93] mb-5">{t('settings.langDialogSub')}</p>
          <div className="space-y-2">
            {Object.entries(LANG_LABEL).map(([code, label]) => {
              const isActive = (settings?.language || 'en') === code;
              return (
                <button
                  key={code}
                  data-testid={`lang-option-${code}`}
                  disabled={langSaving || isActive}
                  onClick={async () => {
                    if (isActive) return;
                    setLangSaving(true);
                    const prev = settings?.language || 'en';
                    setSettings(s => ({ ...(s || {}), language: code }));
                    try {
                      await api.put('/settings/language', { preferred_language: code });
                      i18n.changeLanguage(code);
                      toast.success(`Language set to ${label}`);
                      setLangOpen(false);
                    } catch (e) {
                      setSettings(s => ({ ...(s || {}), language: prev }));
                      toast.error(e?.response?.data?.detail || 'Could not update language');
                    } finally { setLangSaving(false); }
                  }}
                  className={`w-full flex items-center justify-between rounded-xl px-4 py-3 text-left transition-colors ${isActive ? 'bg-[#0F0F10] text-white' : 'bg-[#FAFAFA] hover:bg-[#F4F4F5] text-[#0F0F10]'}`}
                >
                  <span className="text-[14px] font-[700]">{label}</span>
                  {isActive && <CheckCheck size={16} strokeWidth={2.4} />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Location dialog */}
      <Dialog open={locOpen} onOpenChange={setLocOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="location-dialog">
          <DialogTitle className="font-display text-xl font-[600] mb-1">{t('settings.locationTitle')}</DialogTitle>
          <p className="text-xs text-[#8A8A93] mb-5">{t('settings.locDialogSub')}</p>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-2">{t('settings.locCityLabel')}</label>
              <div className="flex gap-2">
                <Input data-testid="settings-city" placeholder={t('settings.locCityPh')}
                  className="rounded-xl bg-white border-[var(--hairline-strong)] text-[#0F0F10] placeholder:text-[#8A8A93] focus-visible:ring-[#B91C1C]/30 px-4 py-3 flex-1"
                  value={loc.city} onChange={e => setLoc(l => ({...l, city: e.target.value, lat: 0, lng: 0}))} />
                <button onClick={detect} data-testid="settings-detect-location"
                  className="w-12 h-12 rounded-xl border border-[var(--hairline-strong)] bg-white flex items-center justify-center hover:bg-black/[0.02]">
                  <MapPin size={18} className="text-[#B91C1C]" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-2">Country</label>
              <select
                data-testid="settings-country"
                className="w-full h-11 rounded-xl bg-white border border-[var(--hairline-strong)] text-[#0F0F10] px-4 text-[15px] focus:outline-none focus:ring-1 focus:ring-[#B91C1C]/30 appearance-none"
                value={loc.country || ''}
                onChange={(e) => setLoc((l) => ({ ...l, country: e.target.value }))}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 14px center',
                  paddingRight: 36,
                }}
              >
                <option value="">Select country…</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-2">{t('settings.locRadiusLabel')} — <span className="text-[#B91C1C] normal-case font-bold">{radius} km</span></label>
              <Slider value={[radius]} onValueChange={v => setRadius(v[0])} min={5} max={200} step={5} data-testid="settings-radius" />
            </div>
            <button onClick={saveLoc} disabled={saving} data-testid="save-location"
              className="btn-shine w-full brand-gradient text-white py-3.5 rounded-full font-semibold text-sm disabled:opacity-50 brand-glow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Save className="inline mr-2 w-4 h-4" /> {t('settings.locSaveBtn')}</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blocked users dialog */}
      <Dialog open={blocksOpen} onOpenChange={setBlocksOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="blocks-dialog">
          <DialogTitle className="font-display text-xl font-[600] mb-4">{t('settings.blocksTitle')}</DialogTitle>
          {blocks.length === 0 ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-[var(--surface-2)] flex items-center justify-center mx-auto mb-3">
                <ShieldBan size={18} className="text-[#8A8A93]" />
              </div>
              <p className="text-sm text-[#4B4B52]">{t('settings.blocksEmpty')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {blocks.map(b => (
                <div key={b.blocked_id} className="flex items-center justify-between py-3 px-4 bg-[var(--surface-2)] rounded-xl">
                  <p className="text-sm font-medium text-[#0F0F10]">{b.display_name}</p>
                  <button onClick={() => unblock(b.blocked_id)} data-testid={`unblock-${b.blocked_id}`}
                    className="text-xs text-red-600 hover:text-red-700 font-semibold">{t('settings.blocksUnblock')}</button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Support */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="support-dialog">
          <div className="w-12 h-12 rounded-full bg-[rgba(185,28,28,0.08)] flex items-center justify-center mb-4">
            <LifeBuoy className="w-6 h-6 text-[#B91C1C]" />
          </div>
          <DialogTitle className="font-display text-xl font-[600] mb-1">{t('settings.supportDialogTitle')}</DialogTitle>
          <p className="text-sm text-[#4B4B52] mb-5 leading-relaxed">{t('settings.supportDialogSub')}</p>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-bold mb-1.5">{t('settings.supportCategoryLabel')}</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: 'bug', label: t('settings.supportCatBug') },
                  { id: 'question', label: t('settings.supportCatQuestion') },
                  { id: 'payment', label: t('settings.supportCatPayment') },
                  { id: 'safety', label: t('settings.supportCatSafety') },
                  { id: 'other', label: t('settings.supportCatOther') },
                ].map(c => (
                  <button key={c.id} type="button"
                    data-testid={`support-cat-${c.id}`}
                    onClick={() => setSupportForm(f => ({ ...f, category: c.id }))}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${
                      supportForm.category === c.id
                        ? 'brand-gradient text-white border-transparent'
                        : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-bold mb-1.5">{t('settings.supportSubjectLabel')}</p>
              <input type="text" maxLength={200}
                data-testid="support-subject"
                placeholder={t('settings.supportSubjectPh')}
                value={supportForm.subject}
                onChange={e => setSupportForm(f => ({ ...f, subject: e.target.value }))}
                className="w-full rounded-xl bg-white border border-[var(--hairline-strong)] text-[#0F0F10] px-3 py-2.5 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#B91C1C]/30" />
            </div>
            <div>
              <p className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-bold mb-1.5">{t('settings.supportMessageLabel')}</p>
              <textarea maxLength={5000} rows={5}
                data-testid="support-message"
                placeholder={t('settings.supportMessagePh')}
                value={supportForm.message}
                onChange={e => setSupportForm(f => ({ ...f, message: e.target.value }))}
                className="w-full rounded-xl bg-white border border-[var(--hairline-strong)] text-[#0F0F10] px-3 py-2.5 text-[14px] resize-none focus:outline-none focus:ring-1 focus:ring-[#B91C1C]/30" />
              <p className="text-[10px] text-zinc-500 text-right mt-1">{supportForm.message.length}/5000</p>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => setSupportOpen(false)}
              className="flex-1 py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold hover:bg-black/[0.02]">{t('settings.cancel')}</button>
            <button
              data-testid="support-submit"
              disabled={supportSubmitting || !supportForm.subject.trim() || !supportForm.message.trim()}
              onClick={async () => {
                setSupportSubmitting(true);
                try {
                  const r = await api.post('/support/ticket', supportForm);
                  toast.success(t('settings.supportSent', { id: r.data.ticket_id || '' }));
                  setSupportForm({ category: 'question', subject: '', message: '' });
                  setSupportOpen(false);
                } catch (e) {
                  toast.error(e.response?.data?.detail || t('settings.supportFailed'));
                } finally {
                  setSupportSubmitting(false);
                }
              }}
              className="flex-1 py-3 rounded-full brand-gradient text-white text-sm font-semibold hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
              {supportSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send size={14}/> {t('settings.send')}</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="delete-account-dialog">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <DialogTitle className="font-display text-xl font-[600] mb-2">{t('settings.deleteDialogTitle')}</DialogTitle>
          <p className="text-sm text-[#4B4B52] mb-5 leading-relaxed">{t('settings.deleteDialogSub')}</p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteOpen(false)} className="flex-1 py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold hover:bg-black/[0.02]">{t('settings.cancel')}</button>
            <button onClick={deleteAccount} disabled={deleting} data-testid="confirm-delete-account"
              className="flex-1 py-3 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('settings.deleteForever')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhoneRow({ settings, onLinkClick }) {
  const { t } = useTranslation();
  const phone = settings?.phone_number;
  const verified = !!settings?.phone_verified;
  return (
    <button
      onClick={onLinkClick}
      data-testid="phone-link-row"
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-black/[0.02] transition-colors text-left group"
    >
      <Phone size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#3F3F46] group-hover:text-[#0F0F10]" />
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">
          {verified ? t('settings.phoneTitle') : t('settings.phoneTitleAdd')}
        </p>
        <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">
          {verified ? t('settings.phoneDescVerified', { phone }) : t('settings.phoneDescAdd')}
        </p>
      </div>
      {verified ? (
        <span className="text-[10px] font-bold tracking-wide text-emerald-700 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
          <BadgeCheck size={11} strokeWidth={2.4} /> {t('settings.phoneVerified')}
        </span>
      ) : (
        <span className="text-[10px] font-bold tracking-wide text-[#B38138] bg-[rgba(179,129,56,0.1)] border border-[#B38138]/20 px-2.5 py-1 rounded-full">
          {t('settings.phoneAdd')}
        </span>
      )}
      <ChevronRight size={14} className="text-[#C4C4CB] group-hover:text-[#4B4B52] group-hover:translate-x-0.5 transition-all flex-shrink-0" strokeWidth={2} />
    </button>
  );
}

function ReadReceiptsRow({ settings, setSettings }) {
  const { t } = useTranslation();
  const enabled = settings?.send_read_receipts ?? true;
  const toggle = async () => {
    const next = !enabled;
    setSettings(s => ({ ...(s || {}), send_read_receipts: next }));
    try { await api.put('/settings/read-receipts', { enabled: next }); }
    catch { setSettings(s => ({ ...(s || {}), send_read_receipts: enabled })); }
  };
  return (
    <div className="w-full flex items-center gap-4 px-5 py-4" data-testid="read-receipts-row">
      <CheckCheck size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#3F3F46]" />
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">{t('settings.readReceiptsTitle')}</p>
        <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{t('settings.readReceiptsDesc')}</p>
      </div>
      <button
        onClick={toggle}
        data-testid="read-receipts-toggle"
        aria-pressed={enabled}
        className={`relative rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-[#B91C1C]' : 'bg-black/15'}`}
        style={{ width: 40, height: 22 }}
      >
        <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${enabled ? 'translate-x-[18px]' : ''}`}/>
      </button>
    </div>
  );
}

function NearbyAlertsRow({ settings, setSettings }) {
  const { t } = useTranslation();
  const enabled = settings?.nearby_alerts_enabled ?? true;
  const toggle = async () => {
    const next = !enabled;
    setSettings(s => ({ ...(s || {}), nearby_alerts_enabled: next }));
    try { await api.put('/settings/nearby-alerts', { enabled: next }); }
    catch { setSettings(s => ({ ...(s || {}), nearby_alerts_enabled: enabled })); }
  };
  return (
    <div className="w-full flex items-center gap-4 px-5 py-4" data-testid="nearby-alerts-row">
      <Sparkles size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#3F3F46]" />
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">{t('settings.nearbyTitle')}</p>
        <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{t('settings.nearbyDesc')}</p>
      </div>
      <button
        onClick={toggle}
        data-testid="nearby-alerts-toggle"
        aria-pressed={enabled}
        className={`relative w-10 h-5.5 h-[22px] rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-[#B91C1C]' : 'bg-black/15'}`}
        style={{ width: 40 }}
      >
        <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${enabled ? 'translate-x-[18px]' : ''}`}/>
      </button>
    </div>
  );
}

function UnitRow() {
  const { t } = useTranslation();
  const [unit, setUnitState] = useState(getUnit());
  const persist = (u) => {
    setUnitState(u);
    saveUnit(u);
    api.put('/profile', { unit_preference: u }).catch(() => {});
  };
  return (
    <div className="w-full flex items-center gap-4 px-5 py-4" data-testid="unit-preference-row">
      <Ruler size={17} strokeWidth={1.6} className="flex-shrink-0 text-[#3F3F46]" />
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">{t('settings.unitsTitle')}</p>
        <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{t('settings.unitsDesc')}</p>
      </div>
      <div className="inline-flex rounded-full border border-[var(--hairline-strong)] overflow-hidden bg-[var(--surface-2)]" role="group">
        <button onClick={() => persist('imperial')} data-testid="unit-imperial"
          className={`px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${unit === 'imperial' ? 'bg-[#0F0F10] text-white' : 'text-[#4B4B52] hover:bg-black/[0.04]'}`}>
          {t('settings.unitsMi')}
        </button>
        <button onClick={() => persist('metric')} data-testid="unit-metric"
          className={`px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${unit === 'metric' ? 'bg-[#0F0F10] text-white' : 'text-[#4B4B52] hover:bg-black/[0.04]'}`}>
          {t('settings.unitsKm')}
        </button>
      </div>
    </div>
  );
}


function EmailPrefRow({ testId, label, desc, checked, onChange }) {
  const isOn = !!checked;
  return (
    <div className="w-full flex items-center gap-4 px-5 py-4" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">{label}</p>
        <p className="text-[12px] text-[#8A8A93] truncate mt-0.5">{desc}</p>
      </div>
      <button
        onClick={() => onChange(!isOn)}
        aria-pressed={isOn}
        disabled={checked === undefined}
        className={`relative h-[22px] rounded-full transition-colors flex-shrink-0 ${isOn ? 'bg-[#B91C1C]' : 'bg-black/15'} ${checked === undefined ? 'opacity-40' : ''}`}
        style={{ width: 40 }}
      >
        <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${isOn ? 'translate-x-[18px]' : ''}`}/>
      </button>
    </div>
  );
}


// Developer — Client-Side Demo Preview toggle. Persists to localStorage via
// /lib/demoMode; interceptor in AuthContext.jsx short-circuits /matches,
// /likes-received, /profile-views, /chat/{id} to fixtures while enabled.
// Reloads once on flip so any in-flight component state clears cleanly.
function DemoModeRow() {
  const [on, setOn] = useState(() => demoMode.isEnabled());
  useEffect(() => demoMode.subscribe(setOn), []);
  const flip = () => {
    demoMode.toggle();
    const nowOn = demoMode.isEnabled();
    toast.success(
      nowOn
        ? `Demo mode ON · ${FIXTURE_STATS.matches} matches, ${FIXTURE_STATS.likes} likes, ${FIXTURE_STATS.messages} messages loaded`
        : 'Demo mode OFF · reverting to live backend',
      { duration: 2600 }
    );
    // Small delay so the toast paints before reload.
    setTimeout(() => window.location.reload(), 600);
  };
  return (
    <div className="w-full flex items-start gap-4 px-5 py-4" data-testid="demo-mode-row">
      <FlaskConical size={17} strokeWidth={1.6} className="flex-shrink-0 mt-0.5 text-[#3F3F46]" />
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold tracking-tight text-[#0F0F10]">
          Client-side demo preview
        </p>
        <p className="text-[12px] text-[#8A8A93] mt-0.5 leading-snug">
          {on
            ? `ON — Matches, Likes, Messages, Views served from static fixtures (${FIXTURE_STATS.liveMatches} live + ${FIXTURE_STATS.ghostMatches} ghost matches). Mutations still hit the live backend.`
            : 'Swap live API responses for static fixtures. Use this to demo or regression-test the UI without a seeded backend.'}
        </p>
      </div>
      <button
        onClick={flip}
        aria-pressed={on}
        data-testid="demo-mode-toggle"
        className={`relative h-[22px] rounded-full transition-colors flex-shrink-0 mt-0.5 ${on ? 'bg-[#B91C1C]' : 'bg-black/15'}`}
        style={{ width: 40 }}
      >
        <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-[18px]' : ''}`}/>
      </button>
    </div>
  );
}
